/** tbl_state_te IDs — keep in sync with backend training_exercise_states */
export const TE_STATE_SCHEDULED = "st202406071447279900";
export const TE_STATE_STARTED = "st202406071447279901";
export const TE_STATE_PAUSED = "st202406071447279902";
export const TE_STATE_FINALIZED = "st202406071447279903";
export const TE_STATE_CANCELLED = "st202406071447279904";

function normalizeTeStateId(stateId) {
  if (!stateId) return null;
  if (stateId === TE_STATE_SCHEDULED) return TE_STATE_SCHEDULED;
  if (stateId === TE_STATE_STARTED) return TE_STATE_STARTED;
  if (stateId === TE_STATE_PAUSED) return TE_STATE_PAUSED;
  if (stateId === TE_STATE_FINALIZED) return TE_STATE_FINALIZED;
  if (stateId === TE_STATE_CANCELLED) return TE_STATE_CANCELLED;
  if (typeof stateId === "string") {
    const s = stateId.trim().toLowerCase();
    if (s === "scheduled") return TE_STATE_SCHEDULED;
    if (s === "started" || s === "running") return TE_STATE_STARTED;
    if (s === "paused") return TE_STATE_PAUSED;
    if (s === "finalized" || s === "finished") return TE_STATE_FINALIZED;
    if (s === "cancelled" || s === "canceled") return TE_STATE_CANCELLED;
  }
  return stateId;
}

export function resolveExerciseStateIdFromRow(ex, realtimeByTeId = {}) {
  if (!ex) return null;
  const rt = realtimeByTeId[ex.te_id];
  if (rt) return normalizeTeStateId(rt);
  if (ex?.tbl_state_te?.st_id) return normalizeTeStateId(ex.tbl_state_te.st_id);
  if (typeof ex?.te_state === "string") return normalizeTeStateId(ex.te_state);
  return normalizeTeStateId(ex?.te_state?.st_id ?? null);
}

/** Build realtime state map from API/sync rows only (ignores stale socket overrides). */
export function buildRealtimeMapFromExerciseRows(exercises) {
  const map = {};
  for (const ex of exercises || []) {
    if (!ex?.te_id) continue;
    const sid = resolveExerciseStateIdFromRow(ex, {});
    if (sid) map[String(ex.te_id)] = sid;
  }
  return map;
}

export function resolveSessionStateId(session) {
  const s = session?.te_state;
  if (!s) return null;
  return typeof s === "string" ? s : s.st_id;
}

/**
 * Prefer the exercise currently running; otherwise the next one not finalized (scheduled / paused).
 */
export function pickCoachControlTargetIndex(exercises, realtimeByTeId = {}) {
  const list = [...(exercises || [])].sort(
    (a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)
  );
  const getSid = (ex) => resolveExerciseStateIdFromRow(ex, realtimeByTeId);
  const runningIdx = list.findIndex((ex) => {
    const sid = getSid(ex);
    return sid === TE_STATE_STARTED || sid === TE_STATE_PAUSED;
  });
  if (runningIdx >= 0) return runningIdx;
  const nextIdx = list.findIndex((ex) => {
    const sid = getSid(ex);
    // Exercises may come back without a state until started; treat null as "scheduled".
    return sid !== TE_STATE_FINALIZED && sid !== TE_STATE_CANCELLED;
  });
  if (nextIdx >= 0) return nextIdx;
  return 0;
}

/** First exercise still in pre-start (scheduled / no state), for table setup focus. */
export function pickFirstNotStartedExerciseIndex(
  exercises,
  realtimeByTeId = {}
) {
  const list = [...(exercises || [])].sort(
    (a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)
  );
  const idx = list.findIndex((ex) => {
    const sid = resolveExerciseStateIdFromRow(ex, realtimeByTeId);
    return !sid || sid === TE_STATE_SCHEDULED;
  });
  if (idx >= 0) return idx;
  return pickCoachControlTargetIndex(exercises, realtimeByTeId);
}

export function pickFirstNotStartedExercise(exercises, realtimeByTeId = {}) {
  const list = [...(exercises || [])].sort(
    (a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)
  );
  const idx = pickFirstNotStartedExerciseIndex(exercises, realtimeByTeId);
  return list[idx] || null;
}

/**
 * "Continue" instead of "Start" once the session or any exercise has left the pure pre-start state.
 */
/**
 * Human-readable state for planning cards (prefers API tbl_state_te names, else i18n keys).
 */
export function getExerciseStateLabel(teStateId, exerciseRow, t, lngBase = "en") {
  if (!teStateId) return "";
  const tbl = exerciseRow?.tbl_state_te;
  if (tbl?.st_id === teStateId) {
    const loc = tbl[`st_name_${lngBase}`];
    if (loc && String(loc).trim()) return String(loc).trim();
    if (tbl.st_name_en && String(tbl.st_name_en).trim()) return String(tbl.st_name_en).trim();
    if (tbl.st_name_fr && String(tbl.st_name_fr).trim()) return String(tbl.st_name_fr).trim();
  }
  const keys = {
    [TE_STATE_SCHEDULED]: "exerciseStateScheduled",
    [TE_STATE_STARTED]: "exerciseStateStarted",
    [TE_STATE_PAUSED]: "exerciseStatePaused",
    [TE_STATE_FINALIZED]: "exerciseStateFinalized",
    [TE_STATE_CANCELLED]: "exerciseStateCancelled",
  };
  return t(keys[teStateId] || "exerciseStateUnknown");
}

export function shouldUseContinueControlLabel(session, exercises, realtimeByTeId = {}) {
  const sessId = resolveSessionStateId(session);
  if (sessId === TE_STATE_STARTED || sessId === TE_STATE_PAUSED) return true;
  const ordered = [...(exercises || [])].sort(
    (a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)
  );
  for (const ex of ordered) {
    const sid = resolveExerciseStateIdFromRow(ex, realtimeByTeId);
    if (!sid) continue;
    if (
      sid === TE_STATE_FINALIZED ||
      sid === TE_STATE_CANCELLED ||
      sid === TE_STATE_STARTED ||
      sid === TE_STATE_PAUSED
    ) {
      return true;
    }
  }
  return false;
}
